<template>
  <div class="container">
    <div class="canvas-container">
      <div v-if="isGenerating" class="generating"><div class="loader" /></div>
      <canvas ref="canvas" width="400" height="400" />
      <canvas
        ref="recorderCanvas"
        width="400"
        height="400"
        class="recorder-canvas"
      />
    </div>
    <a v-if="!animating" download="image.png" ref="download"
      ><button class="form-button" type="button" @click.stop="saveImage">
        Save Image
      </button></a
    >
    <a v-if="canRecord && animating" ref="video"
      ><button class="form-button" type="button" @click.stop="saveVideo">
        {{ recording ? "Saving ..." : "Save Video (Experimental)" }}
      </button></a
    >
  </div>
</template>

<script>
import * as tf from "@tensorflow/tfjs"
import {
  generateImage,
  generateRandomImage,
  getInterpolations,
  getRandomLatentVector
} from "../util.js"

const recordCanvas = (canvas, frames, imgDim, downloadRef, setter) => {
  const fps = 15
  const chunkSize = 100
  const context = canvas.getContext("2d")
  const stream = canvas.captureStream(fps)
  canvas.width = imgDim
  canvas.height = imgDim
  let recordedChunks = []
  const mediaRecorder = new MediaRecorder(stream, { mimeType: "video/webm" })
  mediaRecorder.ondataavailable = e => {
    if (e.data.size > 0) {
      recordedChunks.push(e.data)
    }
  }
  mediaRecorder.onerror = e => {
    window.console.log("MediaRecorder error", e)
    window.alert("An error occured when saving the video")
  }
  mediaRecorder.onstop = e => {
    window.console.log("MediaRecorder stopped", e)
    const blob = new Blob(recordedChunks, { type: "video/webm" })
    const url = window.URL.createObjectURL(blob)
    if (navigator.userAgent.match(/Version\/[\d\.]+.*Safari/)) {
      window.open(url)
    } else {
      downloadRef.href = url
      downloadRef.download = `video.webm`
      downloadRef.click()
      window.URL.revokeObjectURL(url)
    }
    setter(false)
  }
  let i = 0
  mediaRecorder.start(chunkSize)

  setter(true)

  const intervalId = setInterval(() => {
    if (i > frames.length - 2) {
      mediaRecorder.stop()
      clearInterval(intervalId)
    }
    context.putImageData(frames[i], 0, 0)
    i += 1
  }, 1000 / fps)
  return intervalId
}

export default {
  name: "Canvas",
  data() {
    return {
      context: null,
      animationTimer: null,
      images: [],
      recording: false,
      isGenerating: false
    }
  },
  computed: {
    animating() {
      return !!this.animationTimer
    },
    canRecord() {
      return typeof MediaRecorder !== "undefined"
    }
  },
  props: ["config", "modelName"],
  methods: {
    saveImage() {
      const image = this.$refs.canvas.toDataURL("image/png")
      // Safari does not support the HTML download attribute
      if (navigator.userAgent.match(/Version\/[\d\.]+.*Safari/)) {
        window.open(image)
      } else {
        this.$refs.download.setAttribute("href", image)
      }
    },
    saveVideo() {
      const setter = val => (this.recording = val)
      const canvas = this.$refs.recorderCanvas
      const frames = this.images
      const imgDim = this.config.imgDim
      const downloadRef = this.$refs.video
      const intervalId = recordCanvas(
        canvas,
        frames,
        imgDim,
        downloadRef,
        setter
      )
    }
  },
  mounted: async function() {
    this.context = this.$refs.canvas.getContext("2d")
    this.$refs.canvas.width = 1000
    this.$refs.canvas.height = 1000
    this.context.clearRect(
      0,
      0,
      this.$refs.canvas.width,
      this.$refs.canvas.height
    )
    this.context.fillStyle = "black"
    this.context.fillRect(
      0,
      0,
      this.$refs.canvas.width,
      this.$refs.canvas.height
    )
    this.context.fillStyle = "white"
    this.context.font = "bold 64px monospace"
    this.context.fillText(
      "Your creations will",
      130,
      this.$refs.canvas.height / 2 - 32
    )
    this.context.fillText(
      "appear here 👩‍🎨",
      130,
      this.$refs.canvas.height / 2 + 48
    )
  },
  watch: {
    config: async function(newVal, oldVal) {
      clearInterval(this.animationTimer)
      this.animationTimer = null

      const {
        imgDim,
        firstCategory,
        firstContinuous,
        frames = 30,
        interpolate = false,
        secondContinuous,
        fixedLatentNoise,
        scale,
        scaleFrom,
        scaleTo,
        latentVectors = []
      } = newVal

      this.context = this.$refs.canvas.getContext("2d")

      this.isGenerating = true
      this.$emit("generating", this.isGenerating)
      setTimeout(async () => {
        if (interpolate) {
          let z1,
            z2 = null
          let images = []
          if (latentVectors.length > 1) {
            let totalFrames = 0
            for (let i = 1; i < latentVectors.length; i++) {
              totalFrames +=
                i === latentVectors.length - 1 ? frames + 1 : frames
            }
            const _scales = tf.linspace(scaleFrom, scaleTo, totalFrames)
            const scales = _scales.dataSync()
            tf.dispose(_scales)
            z1 = latentVectors[0]
            for (let i = 1; i < latentVectors.length; i++) {
              z2 = latentVectors[i]
              const includeEnd = i === latentVectors.length - 1
              const interpolations = getInterpolations(
                z1,
                z2,
                frames,
                includeEnd
              )
              this.$refs.canvas.width = 500
              this.$refs.canvas.height = 500
              for (let j = 0; j < interpolations.length; j++) {
                this.context.clearRect(
                  0,
                  0,
                  this.$refs.canvas.width,
                  this.$refs.canvas.height
                )
                const interpolation = interpolations[j]
                this.context.fillStyle = "black"
                this.context.fillRect(
                  0,
                  0,
                  this.$refs.canvas.width,
                  this.$refs.canvas.height
                )
                this.context.fillStyle = "white"
                this.context.font = "bold 200px monospace"
                this.context.fillText(
                  `${Math.round(
                    (100 * (images.length + 1)) /
                      ((latentVectors.length - 1) * interpolations.length)
                  )}%`,
                  100,
                  320
                )
                await tf.nextFrame()
                const image = await generateImage(this.modelName, {
                  imgDim,
                  scale: scales[(i - 1) * (interpolations.length - 1) + j],
                  latentVector: interpolation
                })
                images.push(image)
              }
              tf.dispose([z1, interpolations])
              z1 = z2
              tf.dispose([latentVectors])
            }
          } else if (latentVectors.length === 1) {
            const _scales = tf.linspace(scaleFrom, scaleTo, frames)
            const scales = _scales.dataSync()
            tf.dispose(_scales)
            const z = latentVectors[0]
            this.$refs.canvas.width = 500
            this.$refs.canvas.height = 500
            for (let j = 0; j < frames; j++) {
              this.context.clearRect(
                0,
                0,
                this.$refs.canvas.width,
                this.$refs.canvas.height
              )
              this.context.fillStyle = "black"
              this.context.fillRect(
                0,
                0,
                this.$refs.canvas.width,
                this.$refs.canvas.height
              )
              this.context.fillStyle = "white"
              this.context.font = "bold 200px monospace"
              this.context.fillText(
                `${Math.round((100 * (images.length + 1)) / frames)}%`,
                100,
                320
              )
              await tf.nextFrame()
              const image = await generateImage(this.modelName, {
                imgDim,
                scale: scales[j],
                latentVector: z
              })
              images.push(image)
            }
            tf.dispose([z])
          } else {
            z1 = getRandomLatentVector(true)
            z2 = getRandomLatentVector(true)

            const interpolations = getInterpolations(z1, z2, frames)
            this.$refs.canvas.width = 500
            this.$refs.canvas.height = 500
            for (let i = 0; i < interpolations.length; i++) {
              this.context.clearRect(
                0,
                0,
                this.$refs.canvas.width,
                this.$refs.canvas.height
              )
              const interpolation = interpolations[i]
              this.context.fillStyle = "black"
              this.context.fillRect(
                0,
                0,
                this.$refs.canvas.width,
                this.$refs.canvas.height
              )
              this.context.fillStyle = "white"
              this.context.font = "bold 200px monospace"
              this.context.fillText(
                `${Math.round((100 * i) / interpolations.length)}%`,
                100,
                320
              )
              await tf.nextFrame()
              const image = await generateImage(this.modelName, {
                imgDim,
                scale,
                latentVector: interpolation
              })
              images.push(image)
            }
            tf.dispose([z1, z2, interpolations])
          }

          this.$refs.canvas.width = imgDim
          this.$refs.canvas.height = imgDim
          this.images = [...images]
          let currIndex = 0
          this.animationTimer = setInterval(() => {
            this.context.clearRect(0, 0, imgDim, imgDim)
            this.context.putImageData(
              images[currIndex % (images.length - 1)],
              0,
              0
            )
            currIndex += 1
          }, 1000 / 15)
        } else {
          let image = null
          if (latentVectors.length === 1) {
            image = await generateImage(this.modelName, {
              imgDim,
              scale,
              latentVector: latentVectors[0]
            })
            tf.dispose([latentVectors])
          } else {
            image = await generateImage(this.modelName, {
              imgDim,
              firstCategory,
              firstContinuous,
              secondContinuous,
              fixedLatentNoise,
              interpolate,
              scale
            })
          }

          this.$refs.canvas.width = imgDim
          this.$refs.canvas.height = imgDim
          this.context.clearRect(0, 0, imgDim, imgDim)
          this.context.putImageData(image, 0, 0)
        }
        this.isGenerating = false
        this.$emit("generating", this.isGenerating)
      }, 500)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../style/variables.scss";

.container {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-left: 20px;
  align-self: flex-start;
}

.canvas-container {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;
  margin-bottom: 20px;
}

a {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

a > button {
}

.canvas-container {
  border: 2px solid #ededed;
  border-radius: $border-radius;
  box-shadow: $box-shadow-dark-sm;
  background-color: #ededed;
}

canvas {
  width: 50vw;
  height: 50vw;
  image-rendering: pixelated;
}

.generating {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.75);
}

/* https://projects.lukehaas.me/css-loaders/#load1 */
.loader,
.loader:before,
.loader:after {
  background: #ffffff;
  -webkit-animation: load1 1s infinite ease-in-out;
  animation: load1 1s infinite ease-in-out;
  width: 1em;
  height: 4em;
}
.loader {
  color: #ffffff;
  text-indent: -9999em;
  margin: 88px auto;
  position: relative;
  font-size: 11px;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-animation-delay: -0.16s;
  animation-delay: -0.16s;
}
.loader:before,
.loader:after {
  position: absolute;
  top: 0;
  content: "";
}
.loader:before {
  left: -1.5em;
  -webkit-animation-delay: -0.32s;
  animation-delay: -0.32s;
}
.loader:after {
  left: 1.5em;
}
@-webkit-keyframes load1 {
  0%,
  80%,
  100% {
    box-shadow: 0 0;
    height: 4em;
  }
  40% {
    box-shadow: 0 -2em;
    height: 5em;
  }
}
@keyframes load1 {
  0%,
  80%,
  100% {
    box-shadow: 0 0;
    height: 4em;
  }
  40% {
    box-shadow: 0 -2em;
    height: 5em;
  }
}

.recorder-canvas {
  display: none;
}

@media (max-width: 767px) {
  .container {
    margin: 0;
    width: 100%;
  }
  .canvas-container {
    margin: 0;
    margin-top: $margin;
    align-self: stretch;
    margin-bottom: $margin-lg;
    margin-left: $margin;
    margin-right: $margin;
  }
  canvas {
    min-width: 100%;
    height: 100%;
    border: 0;
    padding: 0;
    margin: 0;
  }

  .generating {
    min-width: 100%;
    height: 100%;
  }

  a {
    margin-bottom: 20px;
  }
}
</style>
